import * as React from "react";
import Box from "@mui/material/Box";
import Fab from "@mui/material/Fab";
import Tooltip from "@mui/material/Tooltip";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import CollectionsIcon from "@mui/icons-material/Collections";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import FileUploadDialog from "./FileUploadDialog";
import { getProjectFiles } from "../../apis/apisList/userApi";
import { useLocation, useNavigate } from "react-router-dom";
import { useApp } from "../../Context/AppContext";
import JSZip from "jszip";
import ViewUserFilesModal from "./ViewUserFilesModal";

export default function ActionButtons({
  type,
  project,
  plans,
  id: ID,
  from_new_ui,
}) {
  const [downloading, setDownloading] = React.useState(false);
  const [openFileUpload, setOpenFileUpload] = React.useState(false);
  const [uploadedFiles, setUploadedFiles] = React.useState();
  const [openViewer, setOpenViewer] = React.useState();
  const admin = localStorage.getItem("admin");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setSelectedOption, setProjectData, user } = useApp();
  const generateKMLFiles = (data, type) => {
    data.forEach((plan) => {
      if (plan.selectedOption === type) {
        const fileName = `${project?.contact?.fullName}/${
          project?.name
        }/${plan.selectedOption.replace(/\s+/g, "_")}.kml`; // Example: "Photos_Plan.kml"

        const kmlHeader = `<?xml version="1.0" encoding="UTF-8"?>
        <kml xmlns="http://www.opengis.net/kml/2.2">
          <Document>
        `;
        const kmlFooter = `</Document></kml>`;
        let kmlBody = "";
        plan.features.forEach((feature) => {
          const { geometry, properties } = feature;
          if (geometry.type === "Point") {
            kmlBody += `
              <Placemark>
                <name>Point - ${properties?.id || "Unknown"}</name>
                <Point>
                  <coordinates>${geometry.coordinates[0]},${
              geometry.coordinates[1]
            },0</coordinates>
                </Point>
              </Placemark>
            `;
          } else if (geometry.type === "LineString") {
            kmlBody += `
              <Placemark>
                <name>Line - ${properties?.id || feature.id || "Unknown"}</name>
                <LineString>
                  <coordinates>
                    ${geometry.coordinates
                      .map((coord) => `${coord[0]},${coord[1]},0`)
                      .join(" ")}
                  </coordinates>
                </LineString>
              </Placemark>
            `;
          } else if (geometry.type === "Polygon") {
            kmlBody += `
              <Placemark>
                <name>Polygon - ${
                  properties?.id || feature.id || "Unknown"
                }</name>
                <Polygon>
                  <outerBoundaryIs>
                    <LinearRing>
                      <coordinates>
                        ${geometry.coordinates[0]
                          .map((coord) => `${coord[0]},${coord[1]},0`)
                          .join(" ")}
                      </coordinates>
                    </LinearRing>
                  </outerBoundaryIs>
                </Polygon>
              </Placemark>
            `;
          }
        });

        const kmlContent = kmlHeader + kmlBody + kmlFooter;
        const blob = new Blob([kmlContent], {
          type: "application/vnd.google-earth.kml+xml",
        });

        // Create download link
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    });
  };

  const getFiles = async () => {
    try {
      let res = await getProjectFiles(project?._id, {
        type,
      });

      return res.results;
    } catch (error) {
      console.log("error get files", error);
      return [];
    }
  };

  const handleFileDownload = async (option) => {
    setDownloading(true);
    // Start persistent toast
    const toastId = toast.loading("Downloading files...");
    try {
      const files = await getFiles();
      const fileName = `${project?.contact?.fullName}/${project?.name}/${option}.zip`;
      if (files.length === 0) {
        toast.update(toastId, {
          render: "No files available yet for download",
          type: "info",
          isLoading: false,
          autoClose: 3000,
        });
        return;
      }
      const zip = new JSZip();
      for (const { name, url } of files) {
        const response = await fetch(url);
        const blob = await response.blob();
        zip.file(name, blob);
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });

      saveAs(zipBlob, fileName);

      toast.update(toastId, {
        render: "Download complete!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error) {
      toast.update(toastId, {
        render: "Error downloading files",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
      console.error("Error creating ZIP archive:", error);
    } finally {
      setDownloading(false);
    }
  };

  const handleClick = (option) => {
    if (option === "upload-files") {
      setOpenFileUpload(true);
    }
    if (option === "download-files") {
      handleFileDownload(type);
    }
    if (option === "download-kml") {
      generateKMLFiles(plans, type);
      toast.success("KML file downloaded successfully");
    }
    if (option === "edit") {
      if (!from_new_ui) {
        toast.info(
          "This plan cannot be edited. For more information, contact us at flights@flyover.io or (608) 571-7992.",
          {
            toastId: "plan-edit-info",
            autoClose: 7000,
          }
        );
        return;
      }
      setSelectedOption(type);
      setProjectData(plans);
      localStorage.setItem("edit_project", true);
      localStorage.setItem("edit_id", project?._id);
      localStorage.setItem("edit_project_id", ID);
      localStorage.setItem(
        "projectData",
        JSON.stringify({
          data: plans,
          projectName: project.name,
          address: project.address,
        })
      );

      navigate(`/?redirect=${pathname}`);
    }
  };

  const handleView = () => {
    setOpenViewer(true); // only opens modal
  };

  // const handleView = async () => {
  //   try {
  //     const files = await getFiles();
  //     setUploadedFiles(files);
  //     setOpenViewer(true);
  //   } catch (error) {
  //     toast.error("Error fetching files");
  //   }
  // };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: 2, // Space between buttons
        position: "absolute",
        bottom: 16,
        right: 16,
      }}
    >
      {(project.status === "pending" || admin) && (
        <Tooltip title="Edit Plan" arrow>
          <Fab
            size="small"
            color="inherit"
            aria-label="edit"
            onClick={() => handleClick("edit")}
          >
            <EditIcon />
          </Fab>
        </Tooltip>
      )}
      {admin && (
        <Tooltip title="Upload files" arrow>
          <Fab
            size="small"
            color="inherit"
            aria-label="upload-files"
            onClick={() => handleClick("upload-files")}
          >
            <UploadFileIcon />
          </Fab>
        </Tooltip>
      )}
      {project.status === "completed" && (
        <>
          <Tooltip title="View files" arrow>
            <Fab
              size="small"
              color="inherit"
              aria-label="view-files"
              onClick={handleView}
            >
              <CollectionsIcon />
            </Fab>
          </Tooltip>
          <ViewUserFilesModal
            open={openViewer}
            type={type}
            project={project}
            onClose={() => setOpenViewer(false)}
            getFiles={getFiles} // ✅ pass API fn instead of files
          />
        </>
      )}

      {project.status === "completed" && (
        <Tooltip title="Download files" arrow>
          <Fab
            size="small"
            color="inherit"
            aria-label="download-files"
            onClick={() => handleClick("download-files")}
          >
            <SaveIcon />
          </Fab>
        </Tooltip>
      )}
      {
        /* admin */ true && (
          <Tooltip title="Download KML files" arrow>
            <Fab
              size="small"
              color="inherit"
              aria-label="download-kml"
              onClick={() => handleClick("download-kml")}
            >
              <CloudDownloadIcon />
            </Fab>
          </Tooltip>
        )
      }
      <FileUploadDialog
        open={openFileUpload}
        onClose={() => setOpenFileUpload(false)}
        projectId={project?._id}
        planType={type}
        project={project}
      />
    </Box>
  );
}
